// Khung ung dung: thanh dieu huong ben trai + thanh trang thai o tren.

import { useCallback, useEffect, useState } from "react";
import { NavLink, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { api } from "./api";
import { useAuth } from "./auth";
import { Badge, Spinner } from "./components/ui";
import CaiDatPage from "./pages/CaiDatPage";
import DangNhapPage from "./pages/DangNhapPage";
import KeysPage from "./pages/KeysPage";
import PipelinePage from "./pages/PipelinePage";
import ProductsPage from "./pages/ProductsPage";
import RunsPage from "./pages/RunsPage";
import TaiKhoanPage from "./pages/TaiKhoanPage";
import type { Health } from "./types";

const TRANG = [
  { to: "/quy-trinh", ico: "▶", nhan: "Quy trình" },
  { to: "/san-pham", ico: "▤", nhan: "Sản phẩm" },
  { to: "/khoa-api", ico: "⚿", nhan: "API key" },
  { to: "/lich-su", ico: "◷", nhan: "Lịch sử chạy" },
  { to: "/cai-dat", ico: "⚙", nhan: "Cài đặt" },
  { to: "/tai-khoan", ico: "◕", nhan: "Tài khoản" },
];

/** Cac chip o thanh tren: nhin mot cai la biet thieu gi truoc khi chay. */
function ChipTrangThai({ health }: { health: Health | null }) {
  if (!health) return null;
  const key = health.api_key.gemini + health.api_key.groq;
  return (
    <div className="row wrap" style={{ gap: 7 }}>
      <Badge tone={health.chrome.ok ? "ok" : "danger"} title={health.chrome.duong_dan ?? "không tìm thấy Chrome"}>
        Chrome {health.chrome.ok ? "✓" : "✕"}
      </Badge>
      {/* Phien panel la CUA RIENG tung nguoi. Truoc day cho nay doc file
          .session.json dung chung thoi mot nguoi dung, nen bao sai hoan toan. */}
      <Badge
        tone={health.toi?.co_phien_panel ? "ok" : "warn"}
        title={
          health.toi?.co_phien_panel
            ? "Tài khoản của bạn đã có phiên đăng nhập panel"
            : "Chưa có phiên panel — vào mục Tài khoản để đăng nhập lại"
        }
      >
        Phiên panel {health.toi?.co_phien_panel ? "✓" : "✕"}
      </Badge>
      <Badge tone={key > 0 ? "ok" : "warn"} title="Số API key đang bật">
        {key} API key
      </Badge>
      {!health.scripts.ok ? (
        <Badge tone="danger" title={health.scripts.thieu.join(", ")}>
          Thiếu {health.scripts.thieu.length} script
        </Badge>
      ) : null}
    </div>
  );
}

export default function App() {
  const { toi, dangTai, coPhienPanel, thoat } = useAuth();
  // Ngan keo dieu huong: chi co tac dung o kho hep (CSS lo phan an/hien).
  const [moMenu, setMoMenu] = useState(false);
  const noiDangO = useLocation().pathname;
  const [health, setHealth] = useState<Health | null>(null);
  const [loiHealth, setLoiHealth] = useState<string | null>(null);

  const napHealth = useCallback(() => {
    api
      .health()
      .then((h) => {
        setHealth(h);
        setLoiHealth(null);
      })
      .catch((e) => setLoiHealth(e instanceof Error ? e.message : String(e)));
  }, []);

  // Chuyen trang thi dong ngan keo lai, khong de no che mat noi dung.
  useEffect(() => {
    setMoMenu(false);
  }, [noiDangO]);

  useEffect(() => {
    if (!toi) return;
    napHealth();
    const t = window.setInterval(napHealth, 30000);
    return () => window.clearInterval(t);
  }, [napHealth, toi]);

  if (dangTai) {
    return (
      <div style={{ display: "grid", placeItems: "center", height: "100vh" }}>
        <Spinner />
      </div>
    );
  }

  if (!toi) return <DangNhapPage />;

  return (
    <div className="app">
      {moMenu ? (
        <div className="rail-nen" onClick={() => setMoMenu(false)} aria-hidden />
      ) : null}

      <nav className={`rail ${moMenu ? "mo" : ""}`}>
        <div className="rail-brand">
          <div className="rail-logo">SKU</div>
          <div>
            <div className="rail-name">Auto SKU</div>
            <div className="rail-sub">Chuẩn hoá sản phẩm</div>
          </div>
        </div>

        {TRANG.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            className={({ isActive }) => `rail-link ${isActive ? "active" : ""}`}
          >
            <span className="ico">{t.ico}</span>
            {t.nhan}
            {t.to === "/san-pham" && health?.toi ? (
              <span className="tag">{health.toi.du_lieu.file_finish}</span>
            ) : null}
          </NavLink>
        ))}

        <div className="rail-foot">
          <div className="row" style={{ gap: 8, marginBottom: 6 }}>
            <div
              className="rail-logo"
              style={{ width: 26, height: 26, fontSize: 11, flex: "0 0 26px" }}
            >
              {toi.tai_khoan.slice(0, 2).toUpperCase()}
            </div>
            <div className="grow" style={{ minWidth: 0 }}>
              <div className="small truncate" style={{ fontWeight: 600 }}>
                {toi.tai_khoan}
              </div>
              <div className="small muted">
                {coPhienPanel ? "phiên panel còn" : "phiên panel hết"}
              </div>
            </div>
            <button
              className="btn btn-sm btn-ghost"
              title="Đăng xuất"
              onClick={() => void thoat()}
            >
              ⏻
            </button>
          </div>
          {loiHealth ? (
            <div className="small" style={{ color: "var(--danger)" }}>
              Mất kết nối backend
            </div>
          ) : null}
        </div>
      </nav>

      <div className="main">
        <header className="topbar">
          <button
            className="nut-menu"
            onClick={() => setMoMenu((v) => !v)}
            aria-label="Mở menu"
          >
            ☰
          </button>
          <Routes>
            <Route path="/quy-trinh" element={<h1>Quy trình tự động</h1>} />
            <Route path="/san-pham" element={<h1>Sản phẩm</h1>} />
            <Route path="/khoa-api" element={<h1>API key</h1>} />
            <Route path="/lich-su" element={<h1>Lịch sử chạy</h1>} />
            <Route path="/cai-dat" element={<h1>Cài đặt &amp; dọn dẹp</h1>} />
            <Route path="/tai-khoan" element={<h1>Tài khoản</h1>} />
            <Route path="*" element={<h1>Auto SKU</h1>} />
          </Routes>
          <span className="spacer" />
          <ChipTrangThai health={health} />
        </header>

        <main className="content">
          <Routes>
            <Route path="/" element={<Navigate to="/quy-trinh" replace />} />
            <Route
              path="/quy-trinh"
              element={<PipelinePage health={health} napLaiHealth={napHealth} />}
            />
            <Route path="/san-pham" element={<ProductsPage />} />
            <Route path="/khoa-api" element={<KeysPage napLaiHealth={napHealth} />} />
            <Route path="/lich-su" element={<RunsPage />} />
            <Route path="/cai-dat" element={<CaiDatPage />} />
            <Route path="/tai-khoan" element={<TaiKhoanPage />} />
            <Route path="*" element={<Navigate to="/quy-trinh" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
